import React, { useEffect, useState } from "react";
import NewsLoading from "./newsLoading";
import { utcToLocal } from "../../utils/dateConvert";

export const getNewsReleaseDate = (item) => {
  return item && (item.releasedUtc || item.updatedUtc || item.createdUtc);
};

export const formatNewsDate = (value) => {
  return utcToLocal(value);
};

export const getNewsPreview = (item) => {
  if (!item) {
    return "";
  }

  if (item.summary) {
    return item.summary;
  }

  if (item.contentType === "Message") {
    return item.messageText || "";
  }

  if (item.contentType === "Image") {
    return "Image communication";
  }

  if (item.contentType === "Html") {
    return "HTML communication";
  }

  return "";
};

const escapeNewsTemplateValue = (value) => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const getFriendlyNewsUserName = () => {
  const storedName = localStorage.getItem("accountDisplayName") || localStorage.getItem("userName") || "";
  if (!storedName) return "there";
  if (storedName.indexOf("@") === -1) return storedName;

  const emailName = storedName.split("@")[0]
    .replace(/[._-]+/g, " ")
    .trim();

  if (!emailName) return "there";

  return emailName
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const personalizeNewsHtml = (html) => {
  const values = {
    userName: getFriendlyNewsUserName(),
  };

  return String(html || "").replace(
    /\{\{\s*userName\s*\}\}/g,
    escapeNewsTemplateValue(values.userName)
  );
};

const NewsImageContent = ({ item, compact, onReady }) => {
  const [loadedImage, setLoadedImage] = useState(null);
  const [fitWidth, setFitWidth] = useState(false);
  const [zoom, setZoom] = useState(75);

  if (!item.imageDataUrl) {
    return <NewsLoading />;
  }

  const imageStyle = fitWidth ? { width: "100%" } : { width: `${zoom}%` };

  return (
    <div className={compact ? "news-content news-content-compact" : "news-content"}>
      <div className="news-image-toolbar">
        <button className={fitWidth ? "news-image-tool active" : "news-image-tool"} onClick={() => setFitWidth(!fitWidth)} type="button">
          Fit width
        </button>
        <button className="news-image-tool" disabled={fitWidth || zoom <= 50} onClick={() => setZoom(Math.max(50, zoom - 25))} type="button">
          <i className="fa fa-search-minus"></i>
        </button>
        <span>{fitWidth ? "Auto" : `${zoom}%`}</span>
        <button className="news-image-tool" disabled={fitWidth || zoom >= 200} onClick={() => setZoom(Math.min(200, zoom + 25))} type="button">
          <i className="fa fa-search-plus"></i>
        </button>
        <button className="news-image-tool" onClick={() => { setFitWidth(false); setZoom(75); }} type="button">
          Reset
        </button>
      </div>
      {loadedImage !== item.imageDataUrl && <NewsLoading />}
      <div className={fitWidth ? "news-image-stage fit" : "news-image-stage"}>
        <img className="news-image" src={item.imageDataUrl} alt={item.title || "News communication"} style={imageStyle} onLoad={() => { setLoadedImage(item.imageDataUrl); if (onReady) onReady(); }} />
      </div>
    </div>
  );
};

const NewsHtmlContent = ({ item, compact, onReady }) => {
  const [loadedHtml, setLoadedHtml] = useState(null);
  const ready = !!item.htmlContent && loadedHtml === item.htmlContent;
  if (!item.htmlContent) return <NewsLoading />;
  return (
    <div className={`${compact ? "news-content news-content-compact" : "news-content"} news-html-container`} aria-busy={!ready}>
      {!ready && <div className="news-loading-overlay"><NewsLoading /></div>}
      <iframe
        className="news-html-frame"
        title={item.title || "News communication"}
        sandbox="allow-top-navigation-by-user-activation"
        srcDoc={personalizeNewsHtml(item.htmlContent)}
        style={{ visibility: ready ? "visible" : "hidden" }}
        onLoad={() => { setLoadedHtml(item.htmlContent); if (onReady) onReady(); }}
      />
    </div>
  );
};

const NewsContent = ({ item, compact, onReady }) => {
  useEffect(() => {
    if (item && item.contentType === "Message" && item.messageText && onReady) onReady();
  }, [item, onReady]);

  if (!item) {
    return null;
  }

  if (item.contentType === "Image") {
    return <NewsImageContent key={`${item.id}-${item.version}`} item={item} compact={compact} onReady={onReady} />;
  }

  if (item.contentType === "Html") {
    return <NewsHtmlContent key={`${item.id}-${item.version}`} item={item} compact={compact} onReady={onReady} />;
  }

  return (
    <div className={compact ? "news-content news-content-compact" : "news-content"}>
      <div className="news-message-text">{item.messageText || "Loading communication..."}</div>
    </div>
  );
};

export default NewsContent;
